import type { usePermissions } from '@customerio/hydra-permissions/react';
import type { MergedWorkspace, WorkspaceObjectType } from '../../../../features/workspaces';
import type { AuthStore } from '../../../../stores/auth';
import { AccountFeatureFlag, hasFeatureFlag } from '../../../../stores/auth';
import { PlanFeature } from '../../../../features/auth';
import { AppBundle, AppBundleRoutes } from '../../../../utils/app-bundles';
import type { TargetTransitionOptions } from '../../../../context/safe-navigate';
import type { IconName } from '@customerio/pluma-icons';
import { iconNames } from '@customerio/pluma-icons';
import { AccountState } from '../../../../utils/account';

export interface NavItem {
  id: string;
  label: string;
  iconName?: IconName;
  href?: string;
  children?: NavItem[];
  showNewLabel?: boolean;
  showPremiumLabel?: boolean;
  activeWhenUrls?: string[];
  activeExceptUrls?: string[];
  transitionOptions?: TargetTransitionOptions;
  showSetupGuide?: boolean;
  shouldShowHome?: boolean;
  variant?: 'verdant' | 'evergreen' | 'default';
  onSampleWorkspace?: NavItem;
  showIconOnExpand?: boolean;
  activateOnPipelinesRoutes?: string[];
}

function shouldShowFullJourneysRoutesMenu(auth: AuthStore) {
  const { user, account } = auth;

  if (user == null || account == null) {
    return false;
  }

  // if masqueraded into an account
  if (account?.is_masq ?? false) {
    // if the user doesn't have all permissions properly set to masq return false
    if (user.noMasqAccess || user.masqLevelNotSet) {
      return false;
    }

    // if the user has masq permissions and accessGrantedUntil is set return true
    if (user.isSuperUser || user.canReadWriteMasq || user.canReadOnlyMasq) {
      return true;
    }

    return false;
  }

  return true;
}

export function generateRoutes(
  currentWorkspace: MergedWorkspace,
  auth: AuthStore,
  { hasAnyPermission, hasPermission }: ReturnType<typeof usePermissions>,
  extraConfig?: {
    showSetupGuide: boolean;
    shouldShowHome: boolean;
    sampleWorkspaceId: number;
    objectTypes: WorkspaceObjectType[];
    discoverInAppExperiment: boolean;
  },
): NavItem[][] {
  if (currentWorkspace.cio_workspace_id == null || currentWorkspace.cio_workspace_id === 0) {
    return [];
  }

  const isTrial = auth?.account?.account_state === AccountState.Free;

  const { shouldShowHome, showSetupGuide, objectTypes, sampleWorkspaceId } = extraConfig ?? {
    shouldShowHome: false,
    showSetupGuide: false,
    sampleWorkspaceId: null,
    objectTypes: [],
  };
  const envPrefix = `/workspaces/${currentWorkspace.id}`;
  const envAppPrefix = `${envPrefix}${AppBundleRoutes[AppBundle.Journeys]}`;

  const showFullMenu = shouldShowFullJourneysRoutesMenu(auth);
  const isAnonymousInAppEnabled = auth.account?.planFeatures[PlanFeature.AnonymousInApp].enabled ?? false;
  const isDesignStudioEnabled = hasFeatureFlag(AccountFeatureFlag.ParcelInHydra);

  // show in-app messages to users with the feature flag and permission to create gist in-app messages
  // or if the discoverInAppExperiment is true which is to show them a readonly marketing-ish page
  let showInAppMessages =
    hasFeatureFlag(AccountFeatureFlag.InAppLegacyEditor) && hasPermission('journeys/in_app_message_settings/create');
  showInAppMessages = showInAppMessages || (extraConfig?.discoverInAppExperiment ?? false);
  if (!showFullMenu) {
    return [];
  }

  return [
    [
      {
        id: 'dashboard',
        label: shouldShowHome ? 'Home' : showSetupGuide ? 'Setup Guide' : 'Dashboard',
        href: `${envAppPrefix}/dashboard`,
        iconName: shouldShowHome ? 'home' : showSetupGuide ? 'setup-guide' : 'dashboard',
        activeWhenUrls: [`${envAppPrefix}/dashboard`, `${envAppPrefix}/setup-guide`, `${envAppPrefix}/home`],
        showSetupGuide: showSetupGuide && !shouldShowHome,
      },
      {
        id: 'analysis',
        label: 'Analysis',
        href: `${envAppPrefix}/analysis`,
        iconName: 'analysis',
      },
    ],
    [
      {
        id: 'campaigns',
        label: 'Campaigns',
        href: `${envAppPrefix}/campaigns`,
        iconName: 'campaigns',
      },
      {
        id: 'broadcasts',
        label: 'Broadcasts',
        href: `${envAppPrefix}/broadcasts`,
        iconName: 'broadcast',
      },
      {
        id: 'transactional',
        label: 'Transactional',
        href: `${envAppPrefix}/transactional`,
        iconName: 'transactional-api',
      },
      isAnonymousInAppEnabled
        ? {
            id: 'anonymous',
            label: 'Anonymous',
            href: `${envAppPrefix}/anonymous`,
            iconName: 'anonymous',
          }
        : null,
      {
        id: 'outbox',
        label: 'Deliveries & Drafts',
        href: `${envAppPrefix}/outbox`,
        iconName: 'delivery-drafts',
      },
    ],
    [
      {
        id: 'people',
        label: 'People',
        href: `${envAppPrefix}/people`,
        iconName: 'person',
      },
      ...generateObjectTypeRoutes(envAppPrefix, objectTypes),
      {
        id: 'segments',
        label: 'Segments',
        href: `${envAppPrefix}/segments`,
        iconName: 'segment',
      },
      {
        id: 'logs',
        label: 'Activity Logs',
        href: `${envAppPrefix}/activity_logs`,
        iconName: 'activity',
      },
      {
        id: 'data-integrations',
        label: 'Data & Integrations',
        iconName: 'database',
        children: [
          {
            id: 'data-index',
            label: 'Data Index',
            href: `${envAppPrefix}/data-index`,
          },
          hasAnyPermission('journeys/imports/view', 'journeys/exports/view')
            ? {
                id: 'integrations',
                label: 'Integrations',
                href: `${envAppPrefix}/integrations`,
                activateOnPipelinesRoutes: [AppBundleRoutes[AppBundle.Pipelines]],
              }
            : null,
          hasPermission('journeys/imports/view')
            ? {
                id: 'imports',
                label: 'Imports',
                href: `${envAppPrefix}/imports`,
              }
            : null,
          hasPermission('journeys/exports/view')
            ? {
                id: 'exports',
                label: 'Exports',
                href: `${envAppPrefix}/exports`,
              }
            : null,
        ],
      },
    ],
    [
      {
        id: 'content',
        label: 'Content',
        iconName: 'email-layout',
        children: [
          {
            id: 'message-library',
            label: 'Message Library',
            href: `${envAppPrefix}/message-library`,
          },
          {
            id: 'assets',
            label: 'Assets',
            href: `${envAppPrefix}/assets`,
          },
          {
            id: 'layouts',
            label: 'Email Layouts',
            href: `${envAppPrefix}/layouts`,
          },
          showInAppMessages
            ? {
                id: 'in-app-messages',
                label: 'In-App Messages',
                href: `${envAppPrefix}/in-app-messages`,
              }
            : null,
          {
            id: 'snippets',
            label: 'Snippets',
            href: `${envAppPrefix}/snippets`,
          },
          {
            id: 'collections',
            label: 'Collections',
            href: `${envAppPrefix}/collections`,
            showPremiumLabel: auth.account?.planFeatures[PlanFeature.Collections].showPremiumLabel ?? true,
          },
        ],
      },
      isDesignStudioEnabled
        ? {
            id: 'design-studio',
            label: 'Design Studio',
            iconName: 'cio-parcel',
            href: `${envPrefix}/design-studio/dashboard`,
          }
        : null,
    ],
    auth.account?.id === 1
      ? [
          {
            id: 'internal-tools',
            label: 'Internal Tools',
            iconName: 'a-b-test',
            href: '/internal-tools',
          },
        ]
      : [],
    // Invite Teammates should always be the last the in the nav
    // otherwise it will not render at the bottom of the nav
    isTrial
      ? [
          {
            href: `/workspaces/${sampleWorkspaceId}/journeys`,
            iconName: 'event',
            id: 'view-workspace',
            label: 'View sample workspace',
            showIconOnExpand: false,
            variant: 'verdant',
            onSampleWorkspace: {
              href: `/workspaces/${auth.user?.last_cio_workspace_id}/journeys`,
              label: 'View my workspace',
              showIconOnExpand: true,
              variant: 'default',
            },
          },
          hasPermission('account/team_members/edit')
            ? {
                href: '/settings/team',
                iconName: 'send-auto',
                id: 'invite-teammates',
                label: 'Invite teammates',
                variant: 'evergreen',
              }
            : null,
        ]
      : [],
  ]
    .map((group) => {
      const updatedGroup: NavItem[] = [];

      (group ?? []).forEach((item) => {
        if (item == null) {
          return;
        }

        if ('children' in item && item.children != null) {
          const children = item.children.filter((child) => child != null) as NavItem[];
          updatedGroup.push({ ...item, children } as NavItem);
        } else if (item != null) {
          updatedGroup.push(item as NavItem);
        }
      });

      return updatedGroup;
    })
    .filter((group) => group.length !== 0);
}

export function isNavItemActive(item: NavItem, currentUrl: string) {
  let isActive = false;

  if (item.activeWhenUrls != null) {
    isActive = item.activeWhenUrls.some((url) => currentUrl.startsWith(url));
  }

  if (!isActive && item.href != null) {
    isActive = currentUrl.startsWith(item.href);
  }

  if (isActive && item.activeExceptUrls != null) {
    isActive = !item.activeExceptUrls.some((url) => currentUrl.startsWith(url));
  }

  if (!isActive) {
    const pipelinesRoute = AppBundleRoutes[AppBundle.Pipelines];
    const isPipelinesRoute = pipelinesRoute != null && currentUrl.includes(pipelinesRoute);
    if (isPipelinesRoute && item.activateOnPipelinesRoutes != null && item.activateOnPipelinesRoutes.length > 0) {
      isActive = item.activateOnPipelinesRoutes.some((route) => route != null && currentUrl.includes(route));
    }
  }

  return isActive;
}

function generateObjectTypeRoutes(envAppPrefix: string, workspaceObjectTypes?: WorkspaceObjectType[]) {
  const objectTypes =
    workspaceObjectTypes?.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }

      if (a.name > b.name) {
        return 1;
      }

      return 0;
    }) ?? [];

  if (objectTypes.length === 0) {
    return [
      {
        id: 'objects',
        label: 'Custom Objects',
        href: `${envAppPrefix}/objects`,
        iconName: 'object',
      },
    ];
  }

  if (objectTypes.length <= 3) {
    return objectTypes.map((objectType) => {
      const hasIcon =
        objectType.icon != null && objectType.icon.trim() !== '' && iconNames.some((icon) => icon === objectType.icon);
      const icon = hasIcon ? objectType.icon : 'object';

      return {
        id: `object-types_${objectType.id}`,
        label: objectType.name,
        href: `${envAppPrefix}/objects/${objectType.slug}`,
        iconName: icon,
      };
    });
  }

  return [
    {
      id: 'custom-objects',
      label: 'Custom Objects',
      iconName: 'object',
      children: [
        ...objectTypes.map((objectType) => ({
          id: `object-types_${objectType.id}`,
          label: objectType.name,
          href: `${envAppPrefix}/objects/${objectType.slug}`,
        })),
      ],
    },
  ];
}
